import type { PlacedAircraft3D } from '@/services/3d'
import type { AllocationRenderMode } from '@/types/allocations'
import type { AircraftCatalog, EventPlacement } from '../engine/types'

/** Scene object id for an event aircraft (namespaced so it never collides with real allocations). */
export const sceneIdFor = (eventAllocationId: string) => `evt:${eventAllocationId}`

/** Optional initial transform override so an aircraft can be added mid-motion at pose(t). */
export interface PlacedPoseOverride {
  /** scene feet [x, y, z] — y carries altitude */
  position?: [number, number, number]
  rotationDeg?: number
  renderMode?: AllocationRenderMode
}

/**
 * Map a committed in-memory placement to the universal `PlacedAircraft3D` shape
 * the 3D layer renders. Returns null if the type isn't in the catalog (skipped).
 * Pass `override` to add the aircraft at its current animated pose rather than
 * the static parked spot.
 */
export function eventItemToPlaced(
  p: EventPlacement,
  catalog: AircraftCatalog,
  override?: PlacedPoseOverride
): PlacedAircraft3D | null {
  const entry = catalog.get(p.typeCode)
  if (!entry) return null

  return {
    id: sceneIdFor(p.eventAllocationId),
    aircraftId: p.aircraftId,
    tailNumber: p.tailNumber,
    typeId: entry.typeId,
    position: override?.position ?? [p.position[0], 0, p.position[1]],
    rotation: override?.rotationDeg ?? p.rotation,
    scale: 1,
    aircraftType: entry.aircraftType,
    physicalDimensions: entry.dimensions,
    renderMode: override?.renderMode ?? 'solid',
  }
}
